
import { isDef, tryOnScopeDispose, useTimeoutFn } from '@vueuse/shared'
import { onSnapshot, DocumentReference } from 'firebase/firestore'
import { computed, ref as deepRef, isRef, watch } from 'vue'

import { useLogger } from '@booksy-com/vue-tools'

import {
  toNumberIfNumeric,
} from './utils'

const logger = useLogger('useFirestore')

function getData (docRef) {
  const data = docRef.data()

  if (data) {
    Object.defineProperty(data, 'id', {
      value: toNumberIfNumeric(docRef.id.toString()),
      writable: false,
    })
  }
  return data
}

function getChanges (snapshot) {
  return snapshot.docChanges().map(change => ({
    type: change.type,
    doc: getData(change.doc),
  }))
}

function isDocumentReference (docRef) {
  return docRef instanceof DocumentReference
}

export function useFirestore (
  maybeDocRef,
  initialValue,
  options
) {
  const {
    errorHandler = (err) => logger.error('Firestore error:', err),
    autoDispose = true,
  } = options || {}

  const refOfDocRef = isRef(maybeDocRef)
    ? maybeDocRef
    : computed(() => maybeDocRef)

  let close = () => { }
  const data = deepRef(initialValue)
  const changes = deepRef()

  watch(refOfDocRef, (docRef) => {
    close()
    if (!refOfDocRef.value) {
      data.value = initialValue
    } else if (isDocumentReference(refOfDocRef.value)) {
      close = onSnapshot(docRef, (snapshot) => {
        data.value = getData(snapshot) || null
      }, errorHandler)
    } else {
      close = onSnapshot(docRef, (snapshot) => {
        data.value = snapshot.docs.map(getData).filter(isDef)
        changes.value = getChanges(snapshot)
      }, errorHandler)
    }
  }, { immediate: true })

  if (autoDispose === true) {
    // Dispose the request now.
    tryOnScopeDispose(() => {
      close()
    })
  } else if (typeof autoDispose === 'number') {
    // Dispose the request after timeout.
    tryOnScopeDispose(() => {
      useTimeoutFn(() => {
        close()
      }, autoDispose)
    })
  }

  return { data, changes }
}
